import React, { useState } from 'react';
import { Plus, RefreshCw, DownloadCloud, Filter, SearchIcon, X, HomeIcon, Star, Loader } from '../../icons';

import PropertyCard from './PropertyCard';
import PropertyDetail from './PropertyDetail';
import PropertyModal from './PropertyModal';
import AnalysisModal from '../analysis/AnalysisModal';
import ErrorDisplay from '../common/ErrorDisplay';
import Button from '../ui/Button';
import Chip, { ChipRow } from '../ui/Chip';
import Menu from '../ui/Menu';
import Sheet from '../ui/Sheet';
import EmptyState from '../ui/EmptyState';
import { cn } from '../ui/cn';
import { fetchPropertyData } from '../../services/propertyService';
import { extractPropertyInfo } from '../../utils/propertyPlatforms';
import { exportPropertiesToCSV } from '../../utils/csvExport';

const buildScrapedNotes = (title = '', description = '') => {
  let notes = title ? `Title: ${title}\n` : '';
  if (description) {
    notes += `Description: ${description}`;
  }
  return notes.trim();
};

const mergeNotesWithScrapedAppend = (existingNotes = '', scrapedNotes = '') => {
  const existing = existingNotes.trim();
  const scraped = scrapedNotes.trim();

  if (!existing) return scraped;
  if (!scraped) return existing;
  if (existing.includes(scraped)) return existing;

  if (scraped.length > existing.length * 1.1) {
    return `${existing}\n\nScraped update:\n${scraped}`;
  }

  return existing;
};

const PropertiesContainer = ({
  properties,
  isLoading,
  error,
  onAddProperty,
  onUpdateProperty,
  onDeleteProperty,
  onToggleFavorite,
  onRefetchAll,
  onRefetchProperty,
  onAnalyzeProperty,
  onCompareProperty,
  isRefetchingAll,
  refetchingPropertyId,
  refetchSummary,
  addToast,
}) => {
  // Filter state
  const [filterText, setFilterText] = useState('');
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [filterPropertyType, setFilterPropertyType] = useState('all');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  
  // Sub-tab state for Sale/Rent division
  const [activeSubTab, setActiveSubTab] = useState('all');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [detailPropertyId, setDetailPropertyId] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState('');
  const [currentComparison, setCurrentComparison] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComparing, setIsComparing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({});
  const [isFetchingApiData, setIsFetchingApiData] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Sold properties state — hidden by default
  const [showSold, setShowSold] = useState(false);

  // Refetch summary banner dismissal (keyed on the summary text)
  const [dismissedSummary, setDismissedSummary] = useState('');

  // Filter and separate properties
  const filterProperty = (property) => {
    const matchesText = !filterText || 
      property.title?.toLowerCase().includes(filterText.toLowerCase()) ||
      property.notes?.toLowerCase().includes(filterText.toLowerCase()) ||
      property.url?.toLowerCase().includes(filterText.toLowerCase()) ||
      // Check if search text matches property ID
      (property.propertyId && property.propertyId.includes(filterText)) ||
      // For backward compatibility, extract ID from URL if propertyId doesn't exist
      (!property.propertyId && (() => {
        const { propertyId } = extractPropertyInfo(property.url || '');
        return propertyId && propertyId.includes(filterText);
      })());
    
    const matchesMinPrice = !filterMinPrice || 
      (property.lastKnownPrice && property.lastKnownPrice >= parseInt(filterMinPrice));
    
    const matchesMaxPrice = !filterMaxPrice || 
      (property.lastKnownPrice && property.lastKnownPrice <= parseInt(filterMaxPrice));

    const matchesPropertyType = filterPropertyType === 'all' || 
      (filterPropertyType === 'sale' && property.forSale) ||
      (filterPropertyType === 'rent' && property.forRent) ||
      (filterPropertyType === 'both' && property.forSale && property.forRent);

    const matchesFavorite = !favoritesOnly || !!property.isFavorite;

    return matchesText && matchesMinPrice && matchesMaxPrice && matchesPropertyType && matchesFavorite;
  };

  // Base unsold properties (active listings)
  const baseUnsoldProperties = properties
    .filter((p) => !p.deleted && Number(p.lastKnownPrice) !== 0)
    .filter(filterProperty)
    .map((p) => ({ ...p, sold: false }))
    .sort(
      (a, b) =>
        (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0) ||
        (b.firstTrackedTimestamp?.toDate() || 0) -
        (a.firstTrackedTimestamp?.toDate() || 0)
    );

  // Filter properties based on active sub-tab
  const getFilteredProperties = (subTab) => {
    switch (subTab) {
      case 'sale':
        return baseUnsoldProperties.filter(p => p.forSale);
      case 'rent':
        return baseUnsoldProperties.filter(p => p.forRent);
      case 'both':
        return baseUnsoldProperties.filter(p => p.forSale && p.forRent);
      default:
        return baseUnsoldProperties;
    }
  };

  // Get properties for current sub-tab
  const unsoldProperties = getFilteredProperties(activeSubTab);

  // Get counts for each sub-tab
  const allCount = baseUnsoldProperties.length;
  const saleCount = baseUnsoldProperties.filter(p => p.forSale).length;
  const rentCount = baseUnsoldProperties.filter(p => p.forRent).length;

  // Sold properties (price = 0)
  const soldProperties = properties
    .filter((p) => !p.deleted && Number(p.lastKnownPrice) === 0)
    .filter(filterProperty)
    .map((p) => ({ ...p, sold: true }))
    .sort(
      (a, b) =>
        (b.firstTrackedTimestamp?.toDate() || 0) -
        (a.firstTrackedTimestamp?.toDate() || 0)
    );

  const totalSoldCount = properties.filter((p) => !p.deleted && Number(p.lastKnownPrice) === 0).length;
  const favoriteCount = properties.filter((p) => !p.deleted && Number(p.lastKnownPrice) !== 0 && p.isFavorite).length;

  const visibleProperties = showSold ? [...unsoldProperties, ...soldProperties] : unsoldProperties;

  const hasAdvancedFilters = !!(filterMinPrice || filterMaxPrice || filterPropertyType !== 'all');
  const hasAnyFilter = !!(filterText || hasAdvancedFilters || favoritesOnly || activeSubTab !== 'all');

  const clearFilters = () => {
    setFilterText('');
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterPropertyType('all');
    setActiveSubTab('all');
    setFavoritesOnly(false);
  };

  const handleAddProperty = () => {
    setSelectedProperty(null);
    setFormData({
      forSale: true, // Default to sale
      forRent: false
    });
    setApiError(null);
    setShowAddModal(true);
  };

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setFormData({
      url: property.url || '',
      price: property.lastKnownPrice || '',
      startPrice: property.startPrice || '',
      publishedDate: property.publishedDate || '',
      updatedDate: property.lastReportedUpdateDate || '',
      apiTitle: property.title || '',
      area: property.area || '',
      bedrooms: property.bedrooms || '',
      bathrooms: property.bathrooms || '',
      imageURL: property.imageURL || '',
      notes: property.notes || '',
      forSale: property.forSale || false,
      forRent: property.forRent || false,
    });
    setApiError(null);
    setShowUpdateModal(true);
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
      await onDeleteProperty(propertyId);
      if (detailPropertyId === propertyId) setDetailPropertyId(null);
      if (addToast) {
        addToast('✅ Property deleted successfully!', 'success');
      }
    } catch (error) {
      if (addToast) {
        addToast(`❌ Failed to delete property: ${error.message}`, 'error');
      }
    }
  };

  const handleToggleFavorite = async (propertyId) => {
    try {
      await onToggleFavorite(propertyId);
      // No toast for favorite toggle as it's a quick action
    } catch (error) {
      if (addToast) {
        addToast(`❌ Failed to update favorite: ${error.message}`, 'error');
      }
    }
  };

  const handleAnalyze = async (property) => {
    setSelectedProperty(property);
    setIsAnalyzing(true);
    setShowAnalysisModal(true);
    
    try {
      const analysis = await onAnalyzeProperty(property);
      setCurrentAnalysis(analysis);
    } catch (error) {
      setCurrentAnalysis(`Error analyzing property: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCompare = async (property) => {
    setSelectedProperty(property);
    setIsComparing(true);
    setShowComparisonModal(true);
    
    try {
      const comparison = await onCompareProperty(property, properties);
      setCurrentComparison(comparison);
    } catch (error) {
      setCurrentComparison(`Error comparing property: ${error.message}`);
    } finally {
      setIsComparing(false);
    }
  };

  const handleSubmitProperty = async () => {
    try {
      if (selectedProperty && showUpdateModal) {
        await onUpdateProperty(selectedProperty.id, formData);
        setShowUpdateModal(false);
        if (addToast) {
          addToast('✅ Property updated successfully!', 'success');
        }
      } else {
        // Structure the data properly like in the old implementation
        // Validate price input
        const priceValue = parseFloat(formData.price || "0");
        if (priceValue <= 0 && formData.price && formData.price.trim() !== "") {
          throw new Error("Please enter a valid price greater than 0");
        }

        // Validate property type selection
        if (!formData.forSale && !formData.forRent) {
          throw new Error("Please select at least one property type (For Sale or For Rent)");
        }

        const propertyData = {
          url: formData.url || "",
          price: priceValue,
          publishedDate: formData.publishedDate || "",
          updatedDate: formData.updatedDate || "",
          notes: formData.notes || "",
          forSale: formData.forSale || false,
          forRent: formData.forRent || false,
          additionalFields: {
            title: formData.apiTitle || "",
            area: formData.area || "",
            bedrooms: formData.bedrooms || "",
            bathrooms: formData.bathrooms || "",
            startPrice: formData.startPrice || "",
            imageURL: formData.imageURL || "",
            latitude: formData.latitude || null,
            longitude: formData.longitude || null,
            pets: formData.pets || "",
            furnishing: formData.furnishing || "",
            parking: formData.parking || "",
            constructionYear: formData.constructionYear || "",
            onlineViewing: formData.onlineViewing || "",
            landlordName: formData.landlordName || "",
            landlordJoined: formData.landlordJoined || "",
            ownerAdCount: formData.ownerAdCount || "",
            cityId: formData.cityId || "",
            // Distressed-asset signals (vendor/auction/bank-owned badges)
            currency: formData.currency || "EUR",
            priceOnRequest: formData.priceOnRequest || false,
            sellerType: formData.sellerType || "unknown",
            distress: formData.distress || null,
            isDistressed: formData.isDistressed || false,
          }
        };
        
        const result = await onAddProperty(propertyData);
        setShowAddModal(false);
        if (addToast) {
          if (result?.isNew) {
            addToast('✅ Property added successfully!', 'success');
          } else if (result?.wasDeleted) {
            addToast('✅ Property restored and updated!', 'success');
          } else if (result?.hasUpdate) {
            addToast('✅ Property updated with new information!', 'success');
          } else {
            addToast('ℹ️ Property already exists with same information', 'info');
          }
        }
      }
      setFormData({});
      setSelectedProperty(null);
      setApiError(null);
    } catch (error) {
      setApiError(error.message);
      if (addToast) {
        addToast(`❌ Failed to save property: ${error.message}`, 'error');
      }
    }
  };

  const handleFetchApiData = async () => {
    if (!formData.url) return;
    
    setIsFetchingApiData(true);
    setApiError(null);
    
    try {
      // fetchPropertyData now returns normalized data for any platform
      const data = await fetchPropertyData(formData.url);
      
      // Update form data with normalized fields (platform-agnostic)
      setFormData(prev => ({
        ...prev,
        price: data.price ? data.price.toString() : "0",
        publishedDate: data.publishedDate || "",
        updatedDate: data.updatedDate || "",
        apiTitle: data.title || "",
        area: data.area || "",
        bedrooms: data.bedrooms || "",
        bathrooms: data.bathrooms || "",
        startPrice: data.startPrice || "",
        imageURL: data.imageURL || "",
        // Property type from normalized data
        forSale: data.forSale || false,
        forRent: data.forRent || false,
        // Additional normalized fields
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        pets: data.pets || "N/A",
        furnishing: data.furnishing || "N/A",
        parking: data.parking || "N/A",
        constructionYear: data.constructionYear || "N/A",
        onlineViewing: data.onlineViewing || "N/A",
        landlordName: data.landlordName || "",
        landlordJoined: data.landlordJoined || "",
        ownerAdCount: data.ownerAdCount || "",
        cityId: data.cityId || "",
        // Distressed-asset signals (vendor/auction/bank-owned badges)
        currency: data.currency || "EUR",
        priceOnRequest: data.priceOnRequest || false,
        sellerType: data.sellerType || "unknown",
        distress: data.distress || null,
        isDistressed: data.isDistressed || false,
      }));
      
      // Keep existing notes, but append scraped notes when they are >10% longer.
      setFormData(prev => {
        const existingNotes = prev.notes || "";
        const scrapedNotes = buildScrapedNotes(data.title, data.description);
        const notes = mergeNotesWithScrapedAppend(existingNotes, scrapedNotes);

        return {
          ...prev,
          notes
        };
      });
      
      setApiError(null);
    } catch (error) {
      console.error('Error fetching property data:', error);
      setApiError(error.message);
    } finally {
      setIsFetchingApiData(false);
    }
  };

  const exportToCSV = () => {
    const allFilteredProperties = [...unsoldProperties, ...soldProperties];
    const subTabSuffix = activeSubTab === 'all' ? '' : `-${activeSubTab}`;
    if (!exportPropertiesToCSV(allFilteredProperties, subTabSuffix) && addToast) {
      addToast('No properties to export', 'info');
    }
  };

  // Detail view always reads the freshest copy from the list
  const detailProperty = detailPropertyId
    ? [...unsoldProperties, ...soldProperties].find((p) => p.id === detailPropertyId) ||
      (() => {
        const raw = properties.find((p) => p.id === detailPropertyId && !p.deleted);
        return raw ? { ...raw, sold: Number(raw.lastKnownPrice) === 0 } : null;
      })()
    : null;

  const cardHandlers = (property) => ({
    onOpen: () => setDetailPropertyId(property.id),
    onEdit: () => handleEditProperty(property),
    onDelete: () => handleDeleteProperty(property.id),
    onToggleFavorite: () => handleToggleFavorite(property.id),
    onAnalyze: () => handleAnalyze(property),
    onCompare: () => handleCompare(property),
    onRefetch: onRefetchProperty,
    isRefetching: refetchingPropertyId === property.id,
  });

  const showSummary = refetchSummary && refetchSummary !== dismissedSummary;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" aria-busy="true" aria-label="Loading properties">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-card border border-line rounded-card overflow-hidden animate-pulse">
            <div className="aspect-[16/10] bg-card-2" />
            <div className="p-3.5 space-y-2">
              <div className="h-5 w-28 bg-card-2 rounded" />
              <div className="h-4 w-3/4 bg-card-2 rounded" />
              <div className="h-3 w-1/2 bg-card-2 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Refetch Summary (dismissible) */}
      {showSummary && (
        <div className="bg-card border border-info/40 rounded-card p-3.5 flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
          <pre className="flex-1 whitespace-pre-wrap font-sans text-sm leading-relaxed text-secondary">{refetchSummary}</pre>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissedSummary(refetchSummary)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error Display */}
      <ErrorDisplay error={error} />

      {/* Toolbar: search + actions */}
      <div className="flex items-center gap-2">
        <label className="relative flex-1 min-w-0">
          <SearchIcon className="w-4 h-4 text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Search title, location, ID…"
            aria-label="Search properties"
            className="input pl-10 rounded-full"
          />
        </label>
        <div className="hidden lg:flex items-center gap-2">
          <Button variant="secondary" onClick={() => setShowFilterSheet(true)} className={cn(hasAdvancedFilters && 'border-accent text-accent')}>
            <Filter className="w-4 h-4" /> Filters
          </Button>
          <Menu
            label="Properties actions"
            triggerClassName="h-11 w-11 rounded-xl border border-line bg-card-2 text-primary hover:border-line-strong"
            items={[
              {
                label: isRefetchingAll ? 'Refetching…' : 'Refetch all',
                icon: isRefetchingAll ? Loader : RefreshCw,
                onSelect: onRefetchAll,
                disabled: isRefetchingAll,
              },
              {
                label: 'Export CSV',
                icon: DownloadCloud,
                onSelect: exportToCSV,
                disabled: unsoldProperties.length === 0 && soldProperties.length === 0,
              },
            ]}
          />
          <Button variant="primary" onClick={handleAddProperty}>
            <Plus className="w-4 h-4" /> Track property
          </Button>
        </div>
        <Button
          variant="secondary"
          size="icon"
          aria-label="Filters"
          onClick={() => setShowFilterSheet(true)}
          className={cn('lg:hidden', hasAdvancedFilters && 'border-accent text-accent')}
        >
          <Filter className="w-5 h-5" />
        </Button>
      </div>

      {/* Chips */}
      <ChipRow>
        <Chip active={activeSubTab === 'all' && !favoritesOnly} count={allCount} onClick={() => { setActiveSubTab('all'); setFavoritesOnly(false); }}>
          All
        </Chip>
        <Chip active={activeSubTab === 'sale'} count={saleCount} onClick={() => setActiveSubTab(activeSubTab === 'sale' ? 'all' : 'sale')}>
          For Sale
        </Chip>
        <Chip active={activeSubTab === 'rent'} count={rentCount} onClick={() => setActiveSubTab(activeSubTab === 'rent' ? 'all' : 'rent')}>
          For Rent
        </Chip>
        <Chip active={favoritesOnly} icon={Star} count={favoriteCount} onClick={() => setFavoritesOnly((v) => !v)}>
          Favorites
        </Chip>
        {totalSoldCount > 0 && (
          <Chip active={showSold} toggle count={totalSoldCount} onClick={() => setShowSold((v) => !v)} data-testid="show-sold-chip">
            Show sold
          </Chip>
        )}
        {hasAnyFilter && (
          <button type="button" onClick={clearFilters} className="text-sm text-secondary hover:text-primary underline underline-offset-2 whitespace-nowrap px-1">
            Clear
          </button>
        )}
      </ChipRow>

      {/* Active advanced filters */}
      {hasAdvancedFilters && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {filterMinPrice && <span className="px-2 py-1 rounded-full bg-accent-soft text-accent">from €{Number(filterMinPrice).toLocaleString()}</span>}
          {filterMaxPrice && <span className="px-2 py-1 rounded-full bg-amber-soft text-amber">up to €{Number(filterMaxPrice).toLocaleString()}</span>}
          {filterPropertyType !== 'all' && (
            <span className="px-2 py-1 rounded-full bg-info-soft text-info">
              {filterPropertyType === 'sale' ? 'Sale only' : filterPropertyType === 'rent' ? 'Rent only' : 'Sale & Rent'}
            </span>
          )}
        </div>
      )}

      {/* Grid */}
      {visibleProperties.length === 0 ? (
        <EmptyState
          icon={HomeIcon}
          title={hasAnyFilter ? 'No properties match your filters' : 'No properties tracked yet'}
          description={
            hasAnyFilter
              ? activeSubTab === 'sale'
                ? 'No properties for sale match your filters.'
                : activeSubTab === 'rent'
                  ? 'No properties for rent match your filters.'
                  : 'Try clearing a filter or searching for something else.'
              : 'Paste a listing URL from Bazaraki, BuySell, DOM and more to start tracking prices.'
          }
          action={
            hasAnyFilter ? (
              <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>
            ) : (
              <Button variant="primary" onClick={handleAddProperty}>
                <Plus className="w-4 h-4" /> Track property
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="property-grid">
          {visibleProperties.map((property) => (
            <PropertyCard key={property.id} property={property} {...cardHandlers(property)} />
          ))}
        </div>
      )}

      {/* Mobile FAB */}
      <button
        type="button"
        onClick={handleAddProperty}
        aria-label="Track property"
        className="lg:hidden fixed right-4 bottom-[calc(64px+env(safe-area-inset-bottom)+1rem)] z-30 w-14 h-14 rounded-full bg-accent text-[#062A24] shadow-[0_10px_30px_-8px_rgba(20,211,176,0.8)] flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Filters sheet */}
      <Sheet isOpen={showFilterSheet} onClose={() => setShowFilterSheet(false)} mode="sheet" size="sm" title="Filters" aria-label="Filters">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="filter-min">Min price (€)</label>
              <input id="filter-min" type="number" inputMode="numeric" value={filterMinPrice} onChange={(e) => setFilterMinPrice(e.target.value)} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label" htmlFor="filter-max">Max price (€)</label>
              <input id="filter-max" type="number" inputMode="numeric" value={filterMaxPrice} onChange={(e) => setFilterMaxPrice(e.target.value)} placeholder="∞" className="input" />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="filter-type">Property type</label>
            <select id="filter-type" value={filterPropertyType} onChange={(e) => setFilterPropertyType(e.target.value)} className="input">
              <option value="all">All Types</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Rent</option>
              <option value="both">Both Sale & Rent</option>
            </select>
          </div>
          <div className="lg:hidden space-y-2 pt-2 border-t border-line">
            <Button variant="secondary" className="w-full" onClick={() => { setShowFilterSheet(false); onRefetchAll(); }} disabled={isRefetchingAll} loading={isRefetchingAll}>
              {!isRefetchingAll && <RefreshCw className="w-4 h-4" />} Refetch all
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => { setShowFilterSheet(false); exportToCSV(); }} disabled={unsoldProperties.length === 0 && soldProperties.length === 0}>
              <DownloadCloud className="w-4 h-4" /> Export CSV
            </Button>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="ghost" className="flex-1" onClick={() => { setFilterMinPrice(''); setFilterMaxPrice(''); setFilterPropertyType('all'); }}>
              Reset
            </Button>
            <Button variant="primary" className="flex-1" onClick={() => setShowFilterSheet(false)}>
              Show {visibleProperties.length} {visibleProperties.length === 1 ? 'property' : 'properties'}
            </Button>
          </div>
        </div>
      </Sheet>

      {/* Detail */}
      <PropertyDetail
        property={detailProperty}
        isOpen={!!detailProperty}
        onClose={() => setDetailPropertyId(null)}
        onEdit={handleEditProperty}
        onDelete={handleDeleteProperty}
        onToggleFavorite={handleToggleFavorite}
        onAnalyze={handleAnalyze}
        onCompare={handleCompare}
        onRefetch={onRefetchProperty}
        isRefetching={!!detailProperty && refetchingPropertyId === detailProperty.id}
      />

      {/* Modals */}
      <PropertyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSubmitProperty}
        title="Track new property"
        formData={formData}
        setFormData={setFormData}
        isFetchingApiData={isFetchingApiData}
        onFetchApiData={handleFetchApiData}
        apiError={apiError}
      />

      <PropertyModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSubmit={handleSubmitProperty}
        title="Update property"
        property={selectedProperty}
        formData={formData}
        setFormData={setFormData}
        isFetchingApiData={isFetchingApiData}
        onFetchApiData={handleFetchApiData}
        apiError={apiError}
      />

      <AnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        type="analysis"
        propertyName={selectedProperty?.title || selectedProperty?.apiTitle || 'Property'}
        content={currentAnalysis}
        isLoading={isAnalyzing}
      />

      <AnalysisModal
        isOpen={showComparisonModal}
        onClose={() => setShowComparisonModal(false)}
        type="comparison"
        propertyName={selectedProperty?.title || selectedProperty?.apiTitle || 'Property'}
        content={currentComparison}
        isLoading={isComparing}
      />
    </div>
  );
};

export default PropertiesContainer;
